// Compress a dataURL image by downscaling and re-encoding.
// Used to keep share-link payloads under sensible URL lengths.
// Pass format: 'png' for line-art (floorplans) — lossless keeps walls crisp.
// Default 'jpeg' for photos.
//
// SVG handling: a vector SVG without explicit width/height attrs loads into
// a browser <img> at the default 150px viewport, so drawImage upscales the
// already-rasterized 150px bitmap and produces a blurry PNG. We inject
// explicit width+height attrs on the root <svg> element so the browser
// rasterizes the VECTOR at the target dimensions the first time.
function forceSvgDimensions(svgText, targetDim) {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svg = doc.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') return svgText;
        // Preserve aspect ratio from viewBox if present, otherwise fall back
        // to existing width/height, otherwise square.
        const vb = svg.getAttribute('viewBox');
        let vbW = 0, vbH = 0;
        if (vb) {
            const parts = vb.split(/[\s,]+/).map(Number);
            if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
                vbW = parts[2]; vbH = parts[3];
            }
        }
        if (!vbW || !vbH) {
            vbW = parseFloat(svg.getAttribute('width')) || targetDim;
            vbH = parseFloat(svg.getAttribute('height')) || targetDim;
        }
        const aspect = vbW / vbH;
        const w = aspect >= 1 ? targetDim : Math.round(targetDim * aspect);
        const h = aspect >= 1 ? Math.round(targetDim / aspect) : targetDim;
        svg.setAttribute('width', String(w));
        svg.setAttribute('height', String(h));
        return new XMLSerializer().serializeToString(svg);
    } catch {
        return svgText;
    }
}

export function compressImage(dataURL, maxDim = 1600, quality = 0.65, format = 'jpeg') {
    return new Promise((resolve) => {
        if (!dataURL) { resolve(null); return; }

        const render = (src, isSvg) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                let naturalW = img.naturalWidth || img.width || 1;
                let naturalH = img.naturalHeight || img.height || 1;
                let width, height;
                if (isSvg) {
                    // SVG — rasterize at the actual loaded pixel dims (we
                    // already injected width+height so these now match the
                    // target vector resolution).
                    width = naturalW;
                    height = naturalH;
                } else {
                    width = naturalW;
                    height = naturalH;
                    const longest = Math.max(width, height);
                    if (longest > maxDim) {
                        const scale = maxDim / longest;
                        width = Math.round(width * scale);
                        height = Math.round(height * scale);
                    }
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                try {
                    const mime = format === 'png' ? 'image/png' : 'image/jpeg';
                    const q = format === 'png' ? undefined : quality;
                    resolve(canvas.toDataURL(mime, q));
                } catch {
                    resolve(dataURL);
                }
            };
            img.onerror = () => resolve(dataURL);
            img.src = src;
        };

        // Data URI path: inline SVGs need the same injection.
        if (dataURL.startsWith('data:')) {
            if (dataURL.startsWith('data:image/svg')) {
                try {
                    const comma = dataURL.indexOf(',');
                    const meta = dataURL.slice(5, comma);
                    const payload = dataURL.slice(comma + 1);
                    const text = meta.includes('base64') ? atob(payload) : decodeURIComponent(payload);
                    const forced = forceSvgDimensions(text, maxDim);
                    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(forced)));
                    render(url, true);
                } catch {
                    render(dataURL, true);
                }
            } else {
                render(dataURL, false);
            }
            return;
        }

        // URL path: sniff content-type so we can take the SVG branch.
        fetch(dataURL, { credentials: 'include', method: 'GET' })
            .then(async res => {
                const ct = (res.headers.get('content-type') || '').toLowerCase();
                const isSvg = ct.includes('image/svg') || ct.includes('svg+xml');
                if (isSvg) {
                    const text = await res.text();
                    const forced = forceSvgDimensions(text, maxDim);
                    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(forced)));
                    return { src: url, isSvg: true };
                }
                return { src: dataURL, isSvg: false };
            })
            .then(({ src, isSvg }) => render(src, isSvg))
            .catch(() => render(dataURL, false));
    });
}
